import { Link, Stack, Typography } from '@mui/material';
import { get } from 'lodash';
import React, { FC, useState } from 'react';
import LoadingBackdrop from 'src/components/ui-component/loading/LoadingBackdrop';
import MuiDialog from 'src/components/ui-component/mui-dialog/MuiDialog';
import MuiSnackbar from 'src/components/ui-component/mui-snackbar/MuiSnackbar';
import { useAuthentication } from 'src/context/AuthContext';
import { PostRecord } from 'src/lib/api/PostRecord';
import { APIS } from 'src/lib/constants/apis';
import { ANTRAGS_FORMULAREN, AUSLOESENDE_AKTION, NACHRICHT_ORDNER } from 'src/lib/constants/constant';
import { enumT, getFieldNamefromDataPath } from 'src/utils/helper';

// import schemas
import NachrichtSchema from 'src/lib/paths/nachricht_FULL_Paths.json';
const P = NachrichtSchema.attributes;

const FreischaltungBeantragen: FC<{ selectedProgrammDetails: any; show: boolean; onClose: () => void }> = ({
	selectedProgrammDetails,
	show,
	onClose,
}) => {
	const { authenticatedData } = useAuthentication();

	const antragsformular = get(selectedProgrammDetails, 'antragsFormular', '');
	const foerderProgrammID = get(selectedProgrammDetails, 'foerderProgramm.id', -1);

	const [wsActive, setWsActive] = useState(false);
	const [wsError, setWsError] = useState(false);

	const [showFreischaltungBeantragtPopUp, setShowFreischaltungBeantragtPopUp] = useState('');

	const handleCreateNachricht = async () => {
		try {
			onClose();
			setWsActive(true);

			const nachrichtData = {
				[getFieldNamefromDataPath(P.Foerderprogramm.path)]: foerderProgrammID,
				[getFieldNamefromDataPath(P.Allgemein_Ordner.path)]: NACHRICHT_ORDNER.Postausgang,
				[getFieldNamefromDataPath(P.Antragsteller.path)]: get(authenticatedData, 'organisationId', -1),
				[getFieldNamefromDataPath(P.Allgemein_Betreff.path)]: `Antrag auf Freischaltung "${enumT(antragsformular)}"`,
				[getFieldNamefromDataPath(P.Allgemein_Inhalt.path)]: `Es liegt eine Antrag auf Freischaltung für "${enumT(antragsformular)}"`,
				[getFieldNamefromDataPath(P.Allgemein_Freischaltung_Beantragt_Fuer.path)]: antragsformular,
				[getFieldNamefromDataPath(P.Allgemein_ausloesende_Aktion.path)]: AUSLOESENDE_AKTION.FreischaltungAufAntragstellungBeantragen,
				[getFieldNamefromDataPath(P.Allgemein_Erstellt.path)]: new Date(),
			};

			const res = await PostRecord(APIS['Portal-Nachricht'], { data: nachrichtData });
			if (res) {
				let pushNotificationdata = { data: { entities: [{ api: 'nachrichts', id: get(res, 'data.id') }] } };
				const pushRes = await PostRecord(APIS.PushNotificaiton, pushNotificationdata);
				if (pushRes) setShowFreischaltungBeantragtPopUp(antragsformular);
			}
		} catch (err: any) {
			console.error('ERROR: name ', err);
		} finally {
			setWsActive(false);
		}
	};

	return (
		<>
			<LoadingBackdrop open={wsActive} />
			<MuiSnackbar showSnackbar={wsError} error={wsError} closeSnackbar={() => setWsError(false)} alertMessage='Etwas is schief gelaufen' />
			<MuiDialog
				open={show}
				title={`Freischaltung Antragsformular "${enumT(antragsformular)}" beantragen`}
				content={<FreischaltungBeantragenText antragsformular={antragsformular} />}
				onClose={() => onClose()}
				onConfirmButtonName={'Beantragen'}
				onConfirm={handleCreateNachricht}
			/>

			<MuiDialog
				open={showFreischaltungBeantragtPopUp !== ''}
				title={`Freischaltung Antragsformular "${enumT(showFreischaltungBeantragtPopUp)}" beantragt`}
				content={
					<FreischaltungBeantragtText
						antragsformular={showFreischaltungBeantragtPopUp}
						showFreischaltungBeantragtPopUp={showFreischaltungBeantragtPopUp}
					/>
				}
				onClose={() => {
					setShowFreischaltungBeantragtPopUp('');
				}}
				onCloseButtonName={'Schließen'}
			/>
		</>
	);
};

export default FreischaltungBeantragen;

const FreischaltungBeantragenText: FC<{ antragsformular: string }> = ({ antragsformular }) => {
	switch (antragsformular) {
		case ANTRAGS_FORMULAREN.StartUpStipendium:
			return (
				<>
					<Stack spacing={2}>
						<Typography>
							Haben Sie sich bereits beim StartHub Hessen beworben und anschließend eine Förderempfehlung mit Aufforderung zur
							Antragserstellung erhalten?
						</Typography>
						<Typography>
							<strong>Ja?</strong> Klicken Sie jetzt direkt auf den Button “beantragen”.
						</Typography>
						<Typography>
							<strong>Nein?</strong> Dann ist dies leider nicht möglich. Die Antragstellung ist nur nach erfolgreicher Auswahl im
							Bewerbungsprozess (1. Auswahlstufe) möglich.
						</Typography>
						<Typography>
							Bitte wenden Sie sich bei Fragen an das{' '}
							<Link
								sx={{
									color: 'black',
									textDecoration: 'underline',
									'&:hover': { textDecoration: 'underline', cursor: 'pointer' },
								}}
								href='https://www.starthub-hessen.de/de/'
								target='_blank'
								rel='noopener noreferrer'
							>
								StartHub Hessen
							</Link>
							.
						</Typography>
					</Stack>
				</>
			);
		default:
			return (
				<>
					<Stack spacing={2}>
						<Typography>
							Haben Sie bereits mit unseren Förderexperten persönlich über Ihre Projektidee gesprochen und sich beraten lassen? Das ist der
							erste Schritt.
						</Typography>
						<Typography>
							<strong>Ja?</strong> Kicken Sie jetzt direkt auf den Button “beantragen”.
						</Typography>
						<Typography>
							<strong>Nein?</strong> Dann vereinbaren Sie bitte zunächst einen Termin für Ihr persönliches Beratungsgespräch:
							<strong>zum Kontaktformular </strong>
						</Typography>
					</Stack>
				</>
			);
	}
};

const FreischaltungBeantragtText: FC<{ antragsformular: string; showFreischaltungBeantragtPopUp: string }> = ({
	antragsformular,
	showFreischaltungBeantragtPopUp,
}) => {
	switch (antragsformular) {
		case ANTRAGS_FORMULAREN.StartUpStipendium:
			return (
				<>
					<Stack spacing={2}>
						<Typography>
							Ihre Anfrage zur Freischaltung des Antragsformulars "{enumT(showFreischaltungBeantragtPopUp)}" für Ihr Förderprojekt wurde
							erfolgreich versendet.
						</Typography>
						<Typography>Sie werden automatisch benachrichtigt, sobald Ihre Freischaltung erfolgt ist.</Typography>
					</Stack>
				</>
			);
		default:
			return (
				<>
					<Stack spacing={2}>
						<Typography>
							Ihre Anfrage zur Freischaltung des Antragsformulars "{enumT(showFreischaltungBeantragtPopUp)}" für Ihr Förderprojekt wurde
							erfolgreich versendet.
						</Typography>
						<Typography>Sie werden automatisch benachrichtigt, sobald Ihre Freischaltung erfolgt ist.</Typography>
					</Stack>
				</>
			);
	}
};
